import { useEffect, useMemo } from 'react';
import { Center } from '~/commonComponents/Center';
import { Pressable } from '~/commonComponents/Pressable';
import { Row } from '~/commonComponents/Row';
import { Icon } from '../Icon/Icon';
import styles from './Pagination.module.css';

interface IPaginationProps {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  maxButtons?: number;
}

export const Pagination = ({
  currentPage,
  totalPages,
  onPageChange,
  maxButtons = 5,
}: IPaginationProps) => {
  const showFirstPageNav = currentPage >= 4 && totalPages > maxButtons;
  const showLastPageNav = currentPage <= totalPages - 3 && totalPages > maxButtons;

  const handlePageChange = (page: number) => {
    if (page < 1 || page > totalPages || page === currentPage) return;
    onPageChange(page);
  };

  // Calculate the range of page buttons to display
  const pageRange = useMemo(() => {
    // Ensure we don't show more buttons than there are pages
    const actualMaxButtons = Math.min(maxButtons, totalPages);

    // Calculate half of the max buttons to determine the range on each side of the current page
    const halfMaxButtons = Math.floor(actualMaxButtons / 2);

    let startPage = Math.max(1, currentPage - halfMaxButtons);

    const endPage = Math.min(totalPages, startPage + actualMaxButtons - 1);

    // Adjust startPage if endPage is at the maximum
    if (endPage === totalPages) {
      startPage = Math.max(1, endPage - actualMaxButtons + 1);
    }

    return Array.from({ length: endPage - startPage + 1 }, (_, i) => startPage + i);
  }, [currentPage, totalPages, maxButtons]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [currentPage]);

  if (totalPages <= 1) return null;

  return (
    <div className={styles.paginationContainer}>
      <Row gap={2} marginRight={8}>
        {/* First page button (only visible at page 4) */}
        {showFirstPageNav && (
          <PageControlButton icon="chevron-double-left" onPress={() => handlePageChange(1)} />
        )}

        {/* Previous page button */}
        {currentPage > 1 && (
          <PageControlButton
            icon="chevron-left"
            onPress={() => handlePageChange(currentPage - 1)}
          />
        )}
      </Row>

      {/* Page numbers */}
      {pageRange.map((page) => (
        <button
          key={page}
          className={`${styles.pageButton} ${page === currentPage ? styles.pageButtonActive : ''}`}
          onClick={() => handlePageChange(page)}
        >
          {page}
        </button>
      ))}

      <Row gap={2} marginLeft={8}>
        {/* Next page button */}
        {currentPage !== totalPages && (
          <PageControlButton
            icon="chevron-right"
            onPress={() => handlePageChange(currentPage + 1)}
          />
        )}

        {/* Last page button (not visible on last 3 pages) and not visible if current page is the last page */}
        {showLastPageNav && (
          <PageControlButton
            icon="chevron-double-right"
            onPress={() => handlePageChange(totalPages)}
          />
        )}
      </Row>
    </div>
  );
};

const PageControlButton = ({
  icon,
  onPress,
}: {
  icon: 'chevron-double-right' | 'chevron-double-left' | 'chevron-right' | 'chevron-left';
  onPress: () => void;
}) => {
  return (
    <Pressable onPress={onPress}>
      <Center>
        <Icon name={icon} />
      </Center>
    </Pressable>
  );
};
